import { useCallback, useEffect, useMemo, useState } from "react";
import { ORDEM_ETAPAS } from "../utils/toc/etapas";
import { getItemTenant, setItemTenant, removeItemTenant } from "../utils/tenantStorage";

const STORAGE_KEY_LOG = "pharuerp:kpis-ppcp:log-gargalos";
const STORAGE_KEY_BANNER = "pharuerp:kpis-ppcp:gargalo-banner-lido";
const TAMANHO_MAXIMO_LOG = 60;

function isISO(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value);
}

export function sanitizeEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  if (!ORDEM_ETAPAS.includes(entry.etapa)) return null;
  if (!isISO(entry.data)) return null;

  const numericKeys = [
    "utilizacao",
    "wipTotal",
    "valorWipRS",
    "buffersVermelhos",
    "buffersPretos",
    "throughputAcumuladoMes",
  ];
  const numeric = {};
  for (const key of numericKeys) {
    const value = Number(entry[key]);
    numeric[key] = Number.isFinite(value) ? value : 0;
  }

  return {
    data: entry.data,
    etapa: entry.etapa,
    ...numeric,
  };
}

export function sanitizeLog(input) {
  if (!Array.isArray(input)) return [];
  return input
    .map(sanitizeEntry)
    .filter(Boolean)
    .slice(-TAMANHO_MAXIMO_LOG);
}

export function detectarMudanca(log, gargaloAtual) {
  if (!gargaloAtual || !ORDEM_ETAPAS.includes(gargaloAtual.etapa)) return null;
  const ultimo = log[log.length - 1];
  if (ultimo && ultimo.etapa === gargaloAtual.etapa) return null;
  return { de: ultimo?.etapa ?? null, para: gargaloAtual.etapa };
}

export function montarEntrada(snapshot, agora = new Date()) {
  return {
    data: agora.toISOString(),
    etapa: snapshot.etapa,
    utilizacao: Number(snapshot.utilizacao) || 0,
    wipTotal: Number(snapshot.wipTotal) || 0,
    valorWipRS: Number(snapshot.valorWipRS) || 0,
    buffersVermelhos: Number(snapshot.buffersVermelhos) || 0,
    buffersPretos: Number(snapshot.buffersPretos) || 0,
    throughputAcumuladoMes: Number(snapshot.throughputAcumuladoMes) || 0,
  };
}

function lerLog() {
  if (typeof window === "undefined") return [];
  try {
    const raw = getItemTenant(STORAGE_KEY_LOG);
    return sanitizeLog(raw ? JSON.parse(raw) : []);
  } catch {
    return [];
  }
}

function escreverLog(log) {
  if (typeof window === "undefined") return;
  try {
    setItemTenant(STORAGE_KEY_LOG, JSON.stringify(log));
  } catch {
    // ignore
  }
}

function lerBannerLido() {
  if (typeof window === "undefined") return null;
  try {
    const raw = getItemTenant(STORAGE_KEY_BANNER);
    return raw && isISO(raw) ? raw : null;
  } catch {
    return null;
  }
}

function escreverBannerLido(value) {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      setItemTenant(STORAGE_KEY_BANNER, value);
    } else {
      removeItemTenant(STORAGE_KEY_BANNER);
    }
  } catch {
    // ignore
  }
}

export function useLogGargalos(snapshotAtual) {
  const [log, setLog] = useState(lerLog);
  const [bannerLidoEm, setBannerLidoEm] = useState(lerBannerLido);

  useEffect(() => {
    if (!snapshotAtual) return;
    const mudanca = detectarMudanca(log, snapshotAtual);
    if (!mudanca) return;
    const entrada = montarEntrada(snapshotAtual);
    setLog((prev) => {
      const next = [...prev, entrada].slice(-TAMANHO_MAXIMO_LOG);
      escreverLog(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotAtual?.etapa]);

  const ultimaMudanca = useMemo(() => {
    if (log.length < 2) return null;
    const ultimo = log[log.length - 1];
    const anterior = log[log.length - 2];
    if (ultimo.etapa === anterior.etapa) return null;
    return { de: anterior.etapa, para: ultimo.etapa, em: ultimo.data };
  }, [log]);

  const bannerVisivel = useMemo(() => {
    if (!ultimaMudanca) return false;
    if (!bannerLidoEm) return true;
    return ultimaMudanca.em > bannerLidoEm;
  }, [ultimaMudanca, bannerLidoEm]);

  const marcarBannerLido = useCallback(() => {
    if (!ultimaMudanca) return;
    setBannerLidoEm(ultimaMudanca.em);
    escreverBannerLido(ultimaMudanca.em);
  }, [ultimaMudanca]);

  const limpar = useCallback(() => {
    setLog([]);
    escreverLog([]);
    setBannerLidoEm(null);
    escreverBannerLido(null);
  }, []);

  return {
    log,
    ultimaMudanca,
    bannerVisivel,
    marcarBannerLido,
    limpar,
  };
}

export const TAMANHO_MAXIMO_LOG_GARGALOS = TAMANHO_MAXIMO_LOG;
